import { CHECK_MODULUS, CHECK_VALUE, CROCKFORD_CHECK_ALPHABET, CROCKFORD_DATA_ALPHABET, DATA_SYMBOLS, DATA_VALUE, GROUP_SEPARATOR, GROUP_SIZE, INPUT_ALIASES, TOTAL_SYMBOLS, } from './alphabet.js';
/**
 * Upper-cases, drops hyphens and whitespace, and folds the confusable glyphs.
 *
 * This is the whole of the input tolerance: a code read off a printed pass, dictated over a radio
 * or pasted out of an email all reach the same canonical string.
 */
export function normalizePassInput(raw) {
    let canonical = '';
    for (const character of raw.toUpperCase()) {
        if (character === GROUP_SEPARATOR || /\s/u.test(character)) {
            continue;
        }
        canonical += INPUT_ALIASES.get(character) ?? character;
    }
    return canonical;
}
/**
 * The Crockford check symbol for exactly {@link DATA_SYMBOLS} data symbols.
 *
 * Throws rather than returning a failure: a caller that hands this the wrong number of symbols, or
 * a symbol outside the data alphabet, has a defect rather than bad input, and a check symbol
 * computed over rubbish would be a valid-looking code for a pass that does not exist.
 */
export function checkSymbolFor(data) {
    if (data.length !== DATA_SYMBOLS) {
        throw new RangeError(`expected ${DATA_SYMBOLS} data symbols, received ${data.length}`);
    }
    let accumulator = 0;
    for (const symbol of data) {
        const value = DATA_VALUE.get(symbol);
        if (value === undefined) {
            throw new RangeError(`'${symbol}' is not a Crockford data symbol`);
        }
        accumulator = (accumulator * 32 + value) % CHECK_MODULUS;
    }
    const check = CROCKFORD_CHECK_ALPHABET[accumulator];
    if (check === undefined) {
        throw new RangeError(`no check symbol for residue ${accumulator}`);
    }
    return check;
}
/** `XXXXXXX-XXXXXXX-XXXXXXX`: three groups of seven, for printing and for reading aloud. */
export function formatPassCode(code) {
    const groups = [];
    for (let start = 0; start < code.length; start += GROUP_SIZE) {
        groups.push(code.slice(start, start + GROUP_SIZE));
    }
    return groups.join(GROUP_SEPARATOR);
}
/**
 * Parses operator or scanner input.
 *
 * Structural validity means length AND alphabet AND check symbol. All three together are the
 * predicate the gate's global keystroke capture is built on: it takes the keystrokes only when a
 * complete, valid code has arrived, so typing a name into a search box is never swallowed.
 */
export function parsePassCode(raw) {
    const canonical = normalizePassInput(raw);
    if (canonical.length === 0) {
        return { ok: false, reason: 'EMPTY' };
    }
    if (canonical.length < TOTAL_SYMBOLS) {
        return { ok: false, reason: 'TOO_SHORT' };
    }
    if (canonical.length > TOTAL_SYMBOLS) {
        return { ok: false, reason: 'TOO_LONG' };
    }
    const data = canonical.slice(0, DATA_SYMBOLS);
    const check = canonical.slice(DATA_SYMBOLS);
    for (const symbol of data) {
        if (!DATA_VALUE.has(symbol)) {
            return { ok: false, reason: 'BAD_ALPHABET' };
        }
    }
    if (!CHECK_VALUE.has(check)) {
        return { ok: false, reason: 'BAD_ALPHABET' };
    }
    if (checkSymbolFor(data) !== check) {
        return { ok: false, reason: 'BAD_CHECK_SYMBOL' };
    }
    return { ok: true, code: canonical, data, check };
}
/** Whether a string is a well-formed pass code. Says nothing about whether it exists. */
export function isWellFormedPassCode(raw) {
    return parsePassCode(raw).ok;
}
/**
 * Mints a code from twenty random bytes.
 *
 * The source of entropy is injected rather than imported, for two reasons that both matter. This
 * package runs in the browser and must not reach for a Node built-in; and the server passes
 * `CryptoManager.generateSecureRandom`, which is the only approved source in this system and which
 * refuses a request above 1024 bytes, comfortably outside this use.
 *
 * Each byte is reduced modulo 32, which is unbiased because 256 is a whole multiple of 32. A
 * modulus that did not divide 256 would skew the first few symbols and shrink the keyspace.
 */
export function generatePassCode(randomBytes) {
    const bytes = randomBytes(DATA_SYMBOLS);
    if (bytes.length !== DATA_SYMBOLS) {
        throw new RangeError(`the entropy source returned ${bytes.length} bytes, expected ${DATA_SYMBOLS}`);
    }
    let data = '';
    for (const byte of bytes) {
        const symbol = CROCKFORD_DATA_ALPHABET[byte % 32];
        if (symbol === undefined) {
            throw new RangeError(`no data symbol for byte ${byte}`);
        }
        data += symbol;
    }
    return data + checkSymbolFor(data);
}
//# sourceMappingURL=codec.js.map